<template>
  <!-- Desktop navigation links -->
  <div
    v-if="authStore.isAuthenticated && layout === 'horizontal'"
    class="hidden md:block"
  >
    <div class="ml-10 flex items-baseline space-x-4">
      <router-link
        v-for="item in navigation"
        :key="item.name"
        :to="item.to"
        :class="[
          item.current
            ? 'text-indigo-600 dark:text-indigo-400 border-b-2 border-indigo-600 dark:border-indigo-400 pb-1'
            : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white',
          'px-3 py-2 text-sm font-medium transition-colors'
        ]"
        :aria-current="item.current ? 'page' : undefined"
      >
        {{ t(item.labelKey) }}
      </router-link>
    </div>
  </div>

  <!-- Vertical navigation links -->
  <div
    v-if="authStore.isAuthenticated && layout === 'vertical'"
    class="p-4"
  >
    <div class="space-y-1">
      <router-link
        v-for="item in navigation"
        :key="item.name"
        :to="item.to"
        :class="[
          item.current
            ? 'bg-gray-100 text-gray-900 dark:bg-gray-800 dark:text-white'
            : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-white',
          'block rounded-md px-3 py-2 text-sm font-medium transition-colors'
        ]"
        :aria-current="item.current ? 'page' : undefined"
      >
        {{ t(item.labelKey) }}
      </router-link>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { useI18n } from 'vue-i18n';
import { usePermissions } from '@/composables/usePermissions';
import { useOperationMode } from '@/composables/useOperationMode';
import { hasRestaurantContext } from '@/utils/subdomain';
import { isElectron, platformFeatures } from '@/utils/platform';

interface Props {
  layout?: 'horizontal' | 'vertical';
}

const props = withDefaults(defineProps<Props>(), {
  layout: 'horizontal'
});

const authStore = useAuthStore();
const route = useRoute();
const { t } = useI18n();
const { 
  canAccessCashRegister, 
  canViewSubscription,
  isSysAdmin 
} = usePermissions();
const { isPosOnlyMode } = useOperationMode();

interface NavigationItem {
  name: string;
  labelKey: string;
  to: string;
  current: boolean;
  show: boolean;
}

const navigation = computed((): NavigationItem[] => {
  const path = route.path;
  const isMainDomain = !hasRestaurantContext();
  
  // If on main domain (no subdomain), only show Administration
  if (isMainDomain) {
    const mainDomainNav: NavigationItem[] = [];
    
    // Add SysAdmin links only on main domain
    if (isSysAdmin.value) {
      mainDomainNav.push({ name: 'sysadmin', labelKey: 'app.nav.sysadmin', to: '/sysadmin', current: false, show: true });
      mainDomainNav.push({ name: 'payments', labelKey: 'app.nav.pending_payments', to: '/sysadmin/payments', current: false, show: true });
    }
    
    return mainDomainNav
      .filter(item => item.show)
      .map(item => ({
        ...item,
        current: item.to === path
      }));
  }
  
  // If on subdomain (restaurant context), show restaurant operations
  const baseNav: NavigationItem[] = [
    { name: 'menu', labelKey: 'app.nav.menu', to: '/menu', current: false, show: true },
    { name: 'orders', labelKey: 'app.nav.orders', to: '/orders', current: false, show: !isPosOnlyMode.value },
    { name: 'pos', labelKey: 'app.nav.pos', to: '/pos', current: false, show: isPosOnlyMode.value },
    { name: 'cash-register', labelKey: 'app.nav.cash_register', to: '/cash-register', current: false, show: canAccessCashRegister.value },
    {
      name: 'reports',
      labelKey: 'app.nav.reports',
      to: '/reports',
      current: false,
      show: platformFeatures.cloudOnly.reports && canViewSubscription.value
    },
  ];
  
  // Filter by show property and update current state based on route
  return baseNav
    .filter(item => item.show)
    .map(item => ({
      ...item,
      current: item.to === path || 
               (item.to === '/' && path === '') ||
               (item.to !== '/' && path.startsWith(item.to) && 
                (path === item.to || path.startsWith(`${item.to}/`)))
    }));
});
</script>
